// SPDX-License-Identifier: AGPL-3.0-or-later

const QUIET_WINDOW_MS = 3000;
const MAX_WAIT_MS = 8000;
// How long a flushed group stays replaceable. Without this a group would live for the whole
// session, so a reaction hours later would still count as a replacement and be delivered
// silently against a notification the user has long since dismissed.
const REPLACEMENT_RETENTION_MS = 60000;

export interface ReactionNotificationGroup {
	channelId: string;
	messageId: string;
	reactorId: string;
	emojis: Array<string>;
	content: string | undefined;
	isReplacement: boolean;
}

export type ReactionNotificationFlushCallback = (group: ReactionNotificationGroup) => void;

interface AddParams {
	channelId: string;
	messageId: string;
	reactorId: string;
	emoji: string;
	content?: string | undefined;
}

interface RemoveParams {
	messageId: string;
	reactorId: string;
	emoji: string;
}

interface GroupState {
	channelId: string;
	messageId: string;
	reactorId: string;
	content: string | undefined;
	emojiOrder: Array<string>;
	emojiSet: Set<string>;
	hasFlushed: boolean;
	quietTimer: ReturnType<typeof setTimeout> | null;
	maxTimer: ReturnType<typeof setTimeout> | null;
	evictTimer: ReturnType<typeof setTimeout> | null;
}

export function getReactionGroupKey(messageId: string, reactorId: string): string {
	return `${messageId}:${reactorId}`;
}

export class ReactionNotificationDebouncer {
	private readonly groups = new Map<string, GroupState>();
	private readonly onFlush: ReactionNotificationFlushCallback;

	constructor(onFlush: ReactionNotificationFlushCallback) {
		this.onFlush = onFlush;
	}

	add({channelId, messageId, reactorId, emoji, content}: AddParams): void {
		const key = getReactionGroupKey(messageId, reactorId);
		let group = this.groups.get(key);
		if (!group) {
			group = {
				channelId,
				messageId,
				reactorId,
				content,
				emojiOrder: [],
				emojiSet: new Set(),
				hasFlushed: false,
				quietTimer: null,
				maxTimer: null,
				evictTimer: null,
			};
			this.groups.set(key, group);
		} else if (content !== undefined) {
			group.content = content;
		}
		if (group.evictTimer != null) {
			clearTimeout(group.evictTimer);
			group.evictTimer = null;
		}
		if (!group.emojiSet.has(emoji)) {
			group.emojiSet.add(emoji);
			group.emojiOrder.push(emoji);
		}
		if (group.quietTimer != null) {
			clearTimeout(group.quietTimer);
		}
		group.quietTimer = setTimeout(() => this.flush(key), QUIET_WINDOW_MS);
		if (group.maxTimer == null) {
			group.maxTimer = setTimeout(() => this.flush(key), MAX_WAIT_MS);
		}
	}

	remove({messageId, reactorId, emoji}: RemoveParams): void {
		const key = getReactionGroupKey(messageId, reactorId);
		const group = this.groups.get(key);
		if (!group) return;
		if (!group.emojiSet.has(emoji)) return;
		group.emojiSet.delete(emoji);
		group.emojiOrder = group.emojiOrder.filter((value) => value !== emoji);
		if (group.emojiOrder.length === 0) {
			this.clearGroupTimers(group);
			this.groups.delete(key);
		}
	}

	cancel(key: string): void {
		const group = this.groups.get(key);
		if (!group) return;
		this.clearGroupTimers(group);
		this.groups.delete(key);
	}

	dispose(): void {
		for (const group of this.groups.values()) {
			this.clearGroupTimers(group);
		}
		this.groups.clear();
	}

	private flush(key: string): void {
		const group = this.groups.get(key);
		if (!group) return;
		this.clearGroupTimers(group);
		if (group.emojiOrder.length === 0) {
			this.groups.delete(key);
			return;
		}
		const isReplacement = group.hasFlushed;
		group.hasFlushed = true;
		group.evictTimer = setTimeout(() => this.groups.delete(key), REPLACEMENT_RETENTION_MS);
		this.onFlush({
			channelId: group.channelId,
			messageId: group.messageId,
			reactorId: group.reactorId,
			emojis: [...group.emojiOrder],
			content: group.content,
			isReplacement,
		});
	}

	private clearGroupTimers(group: GroupState): void {
		if (group.quietTimer != null) {
			clearTimeout(group.quietTimer);
			group.quietTimer = null;
		}
		if (group.maxTimer != null) {
			clearTimeout(group.maxTimer);
			group.maxTimer = null;
		}
		if (group.evictTimer != null) {
			clearTimeout(group.evictTimer);
			group.evictTimer = null;
		}
	}
}
